import { auth, authApi, type Session } from "@repo/auth";
import { db, getUserById } from "@repo/database";
import { logger } from "@repo/logs";
import { createServerFn } from "@tanstack/react-start";
import { getRequest, setResponseHeader } from "@tanstack/react-start/server";

/**
 * Get the current session from the request headers.
 * Use this in beforeLoad hooks for authentication checks.
 *
 * Validates that the user still exists in the database to handle stale sessions
 * (e.g., after a database reset where sessions remain in Redis but users are deleted).
 */
export const getSessionFn = createServerFn({ method: "GET" }).handler(
	async (): Promise<Session | null> => {
		const request = getRequest();
		try {
			const session = await auth.api.getSession({
				headers: request.headers,
			});

			if (!session?.user) {
				return null;
			}

			// Verify user still exists in database (handles stale sessions after DB reset)
			const userExists = await getUserById(session.user.id);
			if (!userExists) {
				return null;
			}

			return session as Session | null;
		} catch {
			return null;
		}
	},
);

/**
 * Get the list of organizations for the current user
 */
export const getOrganizationListFn = createServerFn({ method: "GET" }).handler(
	async () => {
		try {
			const request = getRequest();
			const organizationList = await authApi.listOrganizations({
				headers: request.headers,
			});
			return organizationList ?? [];
		} catch (error) {
			logger.error("Failed to get organization list", { error });
			return [];
		}
	},
);

/**
 * Verify an OTP code server-side.
 * better-auth's verifyEmail endpoint only works with "email-verification" type
 * and requires the user to exist. For pre-signup OTP verification (type "sign-in"),
 * we check the verification table directly.
 */
export const verifyOtpFn = createServerFn({ method: "POST" })
	.validator((data: { email: string; otp: string }) => data)
	.handler(async ({ data }) => {
		const identifier = `sign-in-otp-${data.email.toLowerCase()}`;

		const verification = await db.verification.findFirst({
			where: { identifier },
			orderBy: { createdAt: "desc" },
		});

		if (!verification) {
			return { success: false, error: "INVALID_OTP" };
		}

		if (verification.expiresAt < new Date()) {
			return { success: false, error: "OTP_EXPIRED" };
		}

		// OTP is stored as "value:attempts" (e.g. "123456:0")
		const [storedOtp, attempts] = verification.value.split(":");
		const attemptCount = Number.parseInt(attempts || "0", 10);

		if (attemptCount >= 5) {
			await db.verification.delete({ where: { id: verification.id } });
			return { success: false, error: "TOO_MANY_ATTEMPTS" };
		}

		if (storedOtp !== data.otp) {
			// Increment attempt count
			await db.verification.update({
				where: { id: verification.id },
				data: { value: `${storedOtp}:${attemptCount + 1}` },
			});
			return { success: false, error: "INVALID_OTP" };
		}

		// OTP verified — mark as verified (keep record as proof for signup step)
		// Extend expiry by 15 minutes so the user has time to create a password
		const extendedExpiry = new Date(Date.now() + 15 * 60 * 1000);
		await db.verification.update({
			where: { id: verification.id },
			data: {
				value: `verified:${data.email.toLowerCase()}`,
				expiresAt: extendedExpiry,
			},
		});
		return { success: true, error: null };
	});

/**
 * Create an account after OTP verification.
 * Checks for a verified OTP record before calling auth.api.signUpEmail
 * server-side, preventing OTP bypass.
 */
export const signupWithVerifiedEmailFn = createServerFn({ method: "POST" })
	.validator(
		(data: { email: string; password: string; name: string }) => data,
	)
	.handler(async ({ data }) => {
		const email = data.email.toLowerCase();
		const identifier = `sign-in-otp-${email}`;

		// Query specifically for a verified record to avoid race conditions
		// where a concurrent "resend OTP" creates a newer unverified record.
		const verification = await db.verification.findFirst({
			where: { identifier, value: `verified:${email}` },
			orderBy: { createdAt: "desc" },
		});

		if (!verification) {
			return { success: false, error: "EMAIL_NOT_VERIFIED" as const };
		}

		if (verification.expiresAt < new Date()) {
			await db.verification.delete({ where: { id: verification.id } });
			return { success: false, error: "VERIFICATION_EXPIRED" as const };
		}

		try {
			const response = await auth.api.signUpEmail({
				body: {
					email,
					password: data.password,
					name: data.name,
				},
				headers: getRequest().headers,
				asResponse: true,
			});

			if (!response.ok) {
				const body = await response.json().catch(() => null);
				const errorCode =
					body && typeof body === "object" && "code" in body
						? (body.code as string)
						: "SIGNUP_FAILED";
				return { success: false, error: errorCode };
			}

			// Forward Set-Cookie headers to the TanStack Start response
			const cookies = response.headers.getSetCookie();
			if (cookies.length > 0) {
				setResponseHeader("Set-Cookie", cookies);
			}

			// Mark email as verified (signUpEmail sets it to false by default)
			await db.user.updateMany({
				where: { email },
				data: { emailVerified: true },
			});

			// Clean up the verification record
			await db.verification.delete({ where: { id: verification.id } });

			return { success: true, error: null };
		} catch (e) {
			logger.error("Server-side signup failed", { error: e, email });
			return { success: false, error: "SIGNUP_FAILED" };
		}
	});

/**
 * Get the user's connected accounts (OAuth providers)
 */
export const getUserAccountsFn = createServerFn({ method: "GET" }).handler(
	async () => {
		try {
			const request = getRequest();
			const userAccounts = await auth.api.listUserAccounts({
				headers: request.headers,
			});
			return userAccounts;
		} catch (error) {
			logger.error("Failed to get user accounts", { error });
			return [];
		}
	},
);
